import { Nullable } from "common/records";
import { DiscountRecord } from "common/records/Discount";
import { PricePresentationSlideRecord } from "common/records/PricePresentationSlide";
import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { currentOrgPrefConfig } from "common/selectors/org.selectors";
import { List } from "immutable";
import { createSelector } from "reselect";

const rootKey = "discounts";

/**
 * Returns the byId from state
 * @param state The RootState
 * @returns the byId Map
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Returns the discount record for the given id
 * @param state The RootState
 * @param props The discountId
 * @returns the discount record
 */
export const discount = (state: RootState, props: { discountId: number }) => byId(state).get(props.discountId);

/**
 * Returns the ids from ids management
 * @param state The RootState
 * @returns the ids List
 */
export const discountsIds = (state: RootState) => ids(state, { modelName: rootKey });

/**
 * Returns the list discount records for the ids in ids management
 * @param state The RootState
 * @returns the discounts List
 */
export const discounts = createSelector([byId, discountsIds], (byId, discountsIds) => {
  return discountsIds.map((id) => byId.get(id as number)).filter((d) => d) as List<DiscountRecord>;
});

/**
 * Returns the list discount records for the ids in props
 * @param state The RootState
 * @param props The discountIds
 * @returns the discounts List
 */
export const discountsByIds = (state: RootState, props: { discountIds: List<number> }) => {
  return (props.discountIds || List()).map((id) => byId(state).get(id)).filter((d) => d);
};

/**
 * Returns true if discount is used by price presentation
 * @param {RootState} state The RootState
 * @param {number} discountId The discountId
 * @returns boolean value indicating if the discount is used by price presentation slides.
 */
export const discountUsedByPricePresentation = createSelector(
  [
    (state: RootState) => state,
    (state: RootState) =>
      currentOrgPrefConfig(state, {
        path: ["enhanced_price_presentation", "slides"],
      }) as Nullable<List<PricePresentationSlideRecord>>,
    (_: RootState, discountId: number) => discountId,
  ],
  (_state, slides, discountId): boolean => {
    if (!slides) return false;

    return slides.some((slide) => {
      const discountIds = slide.get("discount_ids");
      return discountIds?.includes(discountId);
    });
  },
);
